/**
 * Size guide (1o): tabs, cm/in toggle and current-size row for blocks/size-chart.liquid, plus the
 * "Size guide" trigger the variant picker renders beside the size legend.
 */

const CM_PER_INCH = 2.54;
const MEASUREMENT = /^\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?$/;

/**
 * "84–89" (cm) → "33–35" (in), rounded to the nearest half inch.
 * @param {string} value
 * @returns {string}
 */
export function toInches(value) {
  const text = value.trim();
  if (!MEASUREMENT.test(text)) return value;

  return text.replace(/\d+(?:[.,]\d+)?/g, (number) => {
    const inches = Math.round((Number.parseFloat(number.replace(',', '.')) / CM_PER_INCH) * 2) / 2;
    return Number.isInteger(inches) ? String(inches) : inches.toFixed(1);
  });
}

class SizeChart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    const initialTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    if (initialTab) this.selectTab(initialTab);

    const toggle = this.querySelector('[data-unit-toggle]');
    if (toggle instanceof HTMLElement) toggle.hidden = false;

    this.closest('dialog')?.addEventListener('toggle', this.#markCurrentSize);
    this.#markCurrentSize();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.closest('dialog')?.removeEventListener('toggle', this.#markCurrentSize);
  }

  get tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab instanceof HTMLButtonElement) {
      this.selectTab(tab);
      return;
    }

    const unit = event.target.closest('[data-unit-toggle] [data-unit]');
    if (unit instanceof HTMLButtonElement && unit.dataset.unit) this.setUnit(unit.dataset.unit);
  };

  /** Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern, automatic activation). */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[role="tab"]')) return;

    const { tabs } = this;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    const next = {
      ArrowRight: tabs[(index + 1) % tabs.length],
      ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
      Home: tabs[0],
      End: tabs[tabs.length - 1],
    }[event.key];

    if (!next) return;
    event.preventDefault();
    this.selectTab(next);
    next.focus();
  };

  /** @param {HTMLButtonElement} selected */
  selectTab(selected) {
    for (const tab of this.tabs) {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panel = tab.getAttribute('aria-controls');
      const panelElement = panel ? this.querySelector(`#${CSS.escape(panel)}`) : null;
      if (panelElement instanceof HTMLElement) panelElement.hidden = !isSelected;
    }
  }

  /** @param {string} unit */
  setUnit(unit) {
    this.dataset.unit = unit;

    for (const button of this.querySelectorAll('[data-unit-toggle] [data-unit]')) {
      button.setAttribute('aria-pressed', String(button instanceof HTMLElement && button.dataset.unit === unit));
    }

    for (const cell of this.querySelectorAll('td[data-cm]')) {
      if (!(cell instanceof HTMLElement)) continue;
      const cm = cell.dataset.cm ?? '';
      const inches = cell.dataset.in || toInches(cm);
      cell.textContent = unit === 'in' ? inches : cm;
    }
  }

  /** Tints the row of the size selected in the variant picker, so the table answers "does my size fit". */
  #markCurrentSize = () => {
    const section = this.closest('.shopify-section');
    const trigger = section?.querySelector('[data-size-guide-open]');
    const checked = trigger?.closest('fieldset')?.querySelector('input[type="radio"]:checked');
    const value = checked instanceof HTMLInputElement ? checked.value.trim().toLowerCase() : '';

    for (const row of this.querySelectorAll('tbody tr')) {
      const label = row.querySelector('th')?.textContent?.trim().toLowerCase() ?? '';
      row.classList.toggle('is-current', value !== '' && label === value);
    }
  };
}

if (!customElements.get('size-chart')) {
  customElements.define('size-chart', SizeChart);
}

// The variant picker's "Size guide" trigger lives outside the popup, so it opens the popup directly.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('[data-size-guide-open]');
  if (!trigger) return;

  const popup = trigger.closest('.shopify-section')?.querySelector('dialog-component:has(size-chart:not([data-empty]))');
  if (!popup || !('showDialog' in popup) || typeof popup.showDialog !== 'function') return;

  event.preventDefault();
  popup.showDialog();
});
